import { ToolButton } from '@rcs/ui';
import { useId, useState } from 'react';
import { messages } from '../i18n/ja';

/** 一覧に出すコース。 */
export interface CourseItem {
  readonly id: string;
  readonly name: string;
}

/** 「サンプルから追加」の選択肢。 */
export interface SampleChoice {
  readonly key: string;
  readonly name: string;
}

/**
 * コースの一覧と操作（FR-CRS-001, 002）：編集するコースの選択、名前の変更、新規・複製・削除、
 * サンプルのコースの追加（保存済みのプロジェクトにも、あとから加えたサンプルを足せる）。
 * 名前は Enter またはフォーカスが外れたときに確定し、Esc で元に戻す。部品は 44px 以上。
 */
export function CoursePanel({
  courses,
  currentId,
  canAdd,
  canRemove,
  onSelect,
  onAdd,
  onDuplicate,
  onRemove,
  onRename,
  samples = [],
  onAddSample,
}: {
  readonly courses: readonly CourseItem[];
  readonly currentId: string;
  readonly canAdd: boolean;
  readonly canRemove: boolean;
  readonly onSelect: (id: string) => void;
  readonly onAdd: () => void;
  readonly onDuplicate: () => void;
  readonly onRemove: (id: string) => void;
  /** 名前を変える。反映できなければ `false` */
  readonly onRename: (name: string) => boolean;
  /** 追加できるサンプルのコース */
  readonly samples?: readonly SampleChoice[];
  /** サンプルのコースを追加する */
  readonly onAddSample?: (key: string) => void;
}) {
  const t = messages.courses;
  const selectId = useId();
  const sampleId = useId();
  const current = courses.find((course) => course.id === currentId);
  return (
    <section className="course-panel" aria-label={t.title} data-testid="course-panel">
      <div className="number-field">
        <label htmlFor={selectId} className="number-field__label">
          {t.select}（{t.count.replace('{count}', String(courses.length))}）
        </label>
        <select
          id={selectId}
          value={currentId}
          data-testid="course-select"
          onChange={(event) => {
            onSelect(event.target.value);
          }}
        >
          {courses.map((course) => (
            <option key={course.id} value={course.id}>
              {course.name}
            </option>
          ))}
        </select>
      </div>
      {current && (
        <CourseNameField
          key={`${current.id}:${current.name}`}
          name={current.name}
          onRename={onRename}
        />
      )}
      <div className="course-panel__actions" role="group" aria-label={t.actions}>
        <ToolButton
          label={t.add}
          title={t.addTitle}
          disabled={!canAdd}
          onClick={onAdd}
          data-testid="course-add"
        />
        <ToolButton
          label={t.duplicate}
          disabled={!canAdd}
          onClick={onDuplicate}
          data-testid="course-duplicate"
        />
        <ToolButton
          label={t.remove}
          disabled={!canRemove}
          onClick={() => {
            if (current && window.confirm(t.confirmRemove.replace('{name}', current.name))) {
              onRemove(current.id);
            }
          }}
          data-testid="course-remove"
        />
      </div>
      {samples.length > 0 && onAddSample && (
        <div className="number-field">
          <label htmlFor={sampleId} className="number-field__label">
            {t.addSample}
          </label>
          <select
            id={sampleId}
            value=""
            disabled={!canAdd}
            title={t.addSampleTitle}
            data-testid="course-add-sample"
            onChange={(event) => {
              if (event.target.value !== '') onAddSample(event.target.value);
            }}
          >
            <option value="">{t.addSamplePlaceholder}</option>
            {samples.map((sample) => (
              <option key={sample.key} value={sample.key}>
                {sample.name}
              </option>
            ))}
          </select>
        </div>
      )}
    </section>
  );
}

/** コース名の入力欄。名前が外から変わったときは、親が `key` を変えて作り直す。 */
function CourseNameField({
  name,
  onRename,
}: {
  readonly name: string;
  readonly onRename: (name: string) => boolean;
}) {
  const t = messages.courses;
  const id = useId();
  const [draft, setDraft] = useState(name);
  const [error, setError] = useState(false);
  const commit = (): void => {
    if (draft === name) {
      setError(false);
      return;
    }
    setError(!onRename(draft));
  };
  return (
    <div className="number-field">
      <label htmlFor={id} className="number-field__label">
        {t.name}
      </label>
      <input
        id={id}
        type="text"
        value={draft}
        maxLength={100}
        aria-invalid={error}
        aria-describedby={error ? `${id}-error` : undefined}
        data-testid="course-name"
        onChange={(event) => {
          setDraft(event.target.value);
        }}
        onBlur={commit}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            commit();
          } else if (event.key === 'Escape') {
            setDraft(name);
            setError(false);
          }
        }}
      />
      {error && (
        <p
          id={`${id}-error`}
          className="number-field__error"
          role="alert"
          data-testid="course-name-error"
        >
          {t.nameInvalid}
        </p>
      )}
    </div>
  );
}
